import React, { useEffect } from 'react';
import { log } from 'log';
import { useFocus } from 'js/hooks/useFocus';
import { Icon } from 'js/components/common/Icon';
import { ErrorBoundary } from 'react-error-boundary';
import { cls } from 'js/utils/classNameBuilder.util';
import { useSmartTranslations } from 'js/hooks/i18n';

import { ActionButtons } from './ActionButtons';

import style from './Popup.scss';

const Controls = ({ onPrev, onNext, label, translate }) => {
    const rightIcon = onNext ? 'circleArrowRight' : 'circleArrowRightDisabled';
    const leftIcon = onPrev ? 'circleArrowLeft' : 'circleArrowLeftDisabled';

    return (
        <div className={style.controls}>
            <Icon
                type={leftIcon}
                onClick={onPrev}
                aria-label={translate('playtech.mobile.system.popup.aria-label.arrow.left')}
            />
            <div>
                {label}
            </div>
            <Icon
                type={rightIcon}
                onClick={onNext}
                aria-label={translate('playtech.mobile.system.popup.aria-label.arrow.right')}
            />
        </div>
    );
};

export const ErrorFallback = ({ error }) => {
    log('error', 'Error in popup template', error);

    return null;
};

export const Buttons = ({ buttons, className = '' }) => {
    if (React.isValidElement(buttons)) {
        return buttons;
    }

    if (buttons) {
        return <ActionButtons src={buttons} className={className} />;
    }

    return null;
};

/**
 * ShallowPopup
 * @description General popup layout block. While being popup it does not center itself or have any overlays. Acts as
 * base component for conventional popups, providing some common layout, buttons and icons handling.
 */
export const ShallowPopup = ({
    id,
    onClose,
    buttons,
    children,
    controls,
    title = '',
    onTouchStart,
    className = '',
    classNames = {},
    isModal = false,
    style: popupStyle,
    iconType = 'closePopup',
    refs: { popupRef, innerPopupRef } = {}
}) => {
    const [titleRef, setFocus] = useFocus();
    const translate = useSmartTranslations();
    const buildCls = cls([style, classNames]);

    useEffect(() => setFocus(), [setFocus]);

    return (
        <div
            id={id}
            ref={popupRef}
            style={popupStyle}
            onTouchStart={onTouchStart}
            className={`${style.popup} ${className}`}
        >
            <div className={buildCls(['header'])}>
                <div
                    tabIndex="-1"
                    ref={titleRef}
                    className={`popup-modal__title ${style.title}`}
                >
                    {translate(title)}
                </div>
            </div>
            <div className={`popup-modal__inner-content ${buildCls(['inner'])}`} ref={innerPopupRef}>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                    {children}
                </ErrorBoundary>
            </div>
            <Buttons buttons={buttons} className={classNames.actionButtons} />
            {!isModal && (
                <Icon
                    type={iconType}
                    onClick={onClose}
                    className={buildCls(['close'])}
                    aria-label={translate('playtech.mobile.system.popup.aria-label.close')}
                />
            )}
            {Boolean(controls) && <Controls translate={translate} {...controls} />}
        </div>
    );
};
